import { useMemo } from 'react';
import type {
	FilterDropdownConfigNestedList,
	FilterItem,
	FilterValue,
} from '../types';
import { FilterOperator } from '../types';
import { getValueAsArray } from '../utils';

interface UseFilterDropdownNestedListProps {
	operator: FilterOperator;
	getItems: FilterDropdownConfigNestedList['getItems'];
	value?: FilterValue;
}

export function useFilterDropdownNestedList({
	operator,
	getItems,
	value,
}: UseFilterDropdownNestedListProps) {
	const items: FilterItem[] = useMemo(() => {
		if (operator === FilterOperator.Is) {
			return getItems.filter(
				(item) =>
					item.value &&
					typeof item.value === 'string' &&
					!['tags', 'collections', 'owners', 'teams'].includes(item.value)
			);
		} else if (operator === FilterOperator.Contains) {
			return getItems.filter(
				(item) =>
					item.value &&
					typeof item.value === 'string' &&
					['tags', 'collections', 'owners', 'teams'].includes(item.value)
			);
		}

		throw new Error('Invalid operator or invalid item configuration');
	}, [getItems, operator]);

	const { selected, unselected } = useMemo(() => {
		const values = value ? getValueAsArray(value) : [];
		const { selectedItems, unselectedItems } = items.reduce<{
			selectedItems: FilterItem[];
			unselectedItems: FilterItem[];
		}>(
			(acc, item) => {
				if (values.includes(item.value)) {
					acc.selectedItems.push(item);
				} else {
					acc.unselectedItems.push(item);
				}
				return acc;
			},
			{ selectedItems: [], unselectedItems: [] }
		);

		return {
			selected: selectedItems,
			unselected: unselectedItems,
		};
	}, [items, value]);

	return {
		selected,
		unselected,
	};
}
